import { Stack } from "@mantine/core";
import classNames from "classnames";
import {
  getMimeTypes,
  type AcceptedFileType
} from "../../../../../utils/getMimeTypes";
import {
  DropzoneWithContent,
  type DropzoneWithContentProps
} from "../../../../DropzoneWithContent/DropzoneWithContent";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { HelperText } from "../HelperText/HelperText";
import type { FormFieldTextInputProps } from "./FormFieldTextInput";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

interface FormFieldDropzoneProps
  extends
    CommonFormFieldItemProps,
    Omit<DropzoneWithContentProps, "accept" | "onDrop"> {
  accept?: Array<AcceptedFileType>;
  error?: string;
  label?: FormFieldTextInputProps["label"];
  required?: FormFieldTextInputProps["required"];
  type: "dropzone";
  onDrop?: DropzoneWithContentProps["onDrop"]; // make it optional so we can use form's onChange instead
}

function FormFieldDropzone({
  accept,
  error,
  helpText,
  highlight,
  isBetaFeature,
  label,
  required,
  type,
  warned,
  onDrop,
  ...fieldSpecificProps
}: FormFieldDropzoneProps) {
  const borderColor = error
    ? "var(--mantine-color-error-filled)"
    : warned
      ? "var(--mantine-color-warning-filled)"
      : "var(--mantine-color-neutral-3)";

  return (
    <Stack gap={0}>
      {label && (
        <FormFieldLabel
          helpText={helpText}
          highlight={highlight}
          isBetaFeature={isBetaFeature}
          label={label}
          required={required}
        />
      )}
      <DropzoneWithContent
        {...fieldSpecificProps}
        accept={accept ? getMimeTypes(accept) : undefined}
        aria-label={label?.toString()}
        bd={`1px dashed ${borderColor}`}
        className={classNames(
          "FormFieldDropzone",
          fieldSpecificProps.className
        )}
        maxSize={fieldSpecificProps.maxSize || MAX_FILE_SIZE}
        onDrop={(files) => {
          onDrop?.(files);
        }}
      />
      <HelperText error={error} />
    </Stack>
  );
}

export { FormFieldDropzone, FormFieldDropzoneProps };
